import { AbstractModal } from '../AbstractModal';
import type { HexMapState } from '../../engine/render/renderer';
import type { ContentBuilder } from '../ui/ContentBuilder';
import type { Tile } from '../../engine/hex/types';
import { t } from '../../i18n/translations';
import { buildUnitRoster, getPlayerUnitConfigs } from '../../engine/battle/roster';
import type { UnitGroup } from '../../engine/battle/units';
import { getScoutingTravelTicks, suggestScoutingFood } from '../../engine/scouting/scouting';
import { getDeployedUnits } from '../../engine/scouting/utils';
import {
    ACTION__SCOUT__CONFIRM,
    ACTION__SCOUT__FOOD,
    ACTION__SCOUT__UNIT,
} from '../../engine/input/actionIds';
import { hexDistance } from '../../engine/hex/hexMath';

export class ScoutingModal extends AbstractModal {
    selectedUnits: Record<string, number> = {};
    selectedFood = 0;
    private originSettlementId: string;
    private targetTile: Tile;

    constructor(
        rect: { x: number; y: number; w: number; h: number },
        tile: Tile,
        originId: string,
    ) {
        super(tile, rect, originId);
        this.id = `scout_modal_${tile.id}`;
        this.originSettlementId = originId;
        this.targetTile = tile;
        this.showTileHighlight = true;
        this.allowCloseDuringTutorial = true;
    }

    getHeaderLabel(state: HexMapState): string {
        return t(state.config.ui.language, 'scout.title');
    }

    handleAction(actionId: string, state: HexMapState): boolean {
        if (actionId.startsWith(`${ACTION__SCOUT__UNIT}:`)) {
            const [, unitId, direction] = actionId.split(':');
            if (!unitId || (direction !== 'plus' && direction !== 'minus')) return false;
            const current = this.selectedUnits[unitId] ?? 0;
            const next = direction === 'plus' ? current + 1 : Math.max(0, current - 1);
            this.selectedUnits[unitId] = next;
            return true;
        }
        if (actionId.startsWith(`${ACTION__SCOUT__FOOD}:`)) {
            const [, direction] = actionId.split(':');
            if (direction !== 'plus' && direction !== 'minus') return false;
            const step = state.config.scouting.foodStep;
            this.selectedFood =
                direction === 'plus'
                    ? this.selectedFood + step
                    : Math.max(0, this.selectedFood - step);
            return true;
        }
        if (actionId === ACTION__SCOUT__CONFIRM) {
            return sendScoutingMission(state, this.targetTile, this.originSettlementId, this);
        }
        return false;
    }

    buildContent(state: HexMapState, builder: ContentBuilder): void {
        const lang = state.config.ui.language;
        const settlement = state.settlementsById[this.originSettlementId];
        if (!settlement) {
            builder.text(t(lang, 'scout.missingSettlement'));
            return;
        }
        const roster = buildUnitRoster(state.config);
        const deployed = getDeployedUnits(state, settlement.id);
        const unitConfigs = getPlayerUnitConfigs(state.config);
        const distance = hexDistance(settlement.tilePosition, this.targetTile.position);
        const selectedGroups = toGroups(this.selectedUnits);
        const suggestedFood = suggestScoutingFood(
            selectedGroups,
            roster,
            distance,
            state.config.scouting,
        );

        builder.section(t(lang, 'scout.destination'), (section) => {
            section.text(t(lang, 'scout.tile', this.targetTile.id));
            section.text(t(lang, 'scout.distance', distance));
        });

        builder.section(t(lang, 'scout.units'), (section) => {
            if (unitConfigs.length === 0) {
                section.text(t(lang, 'scout.unitsNone'));
                return;
            }
            unitConfigs.forEach((unit) => {
                const available = getAvailableUnitCount(
                    settlement,
                    unit.id,
                    deployed[unit.id] || 0,
                );
                const selected = this.selectedUnits[unit.id] ?? 0;
                section.text(
                    t(lang, 'scout.unitLine', t(lang, unit.labelKey), selected, available),
                );
                section.button(
                    `${ACTION__SCOUT__UNIT}:${unit.id}:plus`,
                    t(lang, 'scout.unitPlus'),
                    selected < available,
                );
                section.button(
                    `${ACTION__SCOUT__UNIT}:${unit.id}:minus`,
                    t(lang, 'scout.unitMinus'),
                    selected > 0,
                );
                section.spacer(4);
            });
        });

        builder.section(t(lang, 'scout.food'), (section) => {
            section.text(t(lang, 'scout.foodAvailable', settlement.inventory.food));
            section.text(t(lang, 'scout.foodSelected', this.selectedFood));
            section.text(t(lang, 'scout.foodSuggested', suggestedFood));
            section.button(`${ACTION__SCOUT__FOOD}:plus`, t(lang, 'scout.foodPlus'), true);
            section.button(
                `${ACTION__SCOUT__FOOD}:minus`,
                t(lang, 'scout.foodMinus'),
                this.selectedFood > 0,
            );
        });

        const canSend = canSendScouts(
            state,
            settlement,
            selectedGroups,
            this.selectedFood,
            deployed,
        );
        builder.section(t(lang, 'scout.launch'), (section) => {
            section.button(ACTION__SCOUT__CONFIRM, t(lang, 'scout.send'), canSend);
        });
    }
}

function toGroups(selected: Record<string, number>): UnitGroup[] {
    return Object.entries(selected)
        .filter(([, count]) => count > 0)
        .map(([type, count]) => ({ type, count }));
}

function getAvailableUnitCount(
    settlement: HexMapState['settlementsById'][string],
    unitId: string,
    deployed: number,
): number {
    if (unitId !== 'militia') return 0;
    const total = settlement.jobs.militia;
    return Math.max(0, total - deployed);
}

function canSendScouts(
    state: HexMapState,
    settlement: HexMapState['settlementsById'][string],
    selected: UnitGroup[],
    food: number,
    deployed: Record<string, number>,
): boolean {
    const roster = buildUnitRoster(state.config);
    const totalUnits = selected.reduce((sum, group) => sum + group.count, 0);
    if (totalUnits <= 0) return false;
    const weaponsRequired = selected.reduce((sum, group) => {
        const unit = roster.get(group.type);
        if (!unit) return sum;
        return sum + unit.weaponCost * group.count;
    }, 0);
    if (settlement.inventory.weapons < weaponsRequired) return false;
    if (settlement.inventory.food < food) return false;
    const availableMilitia = getAvailableUnitCount(settlement, 'militia', deployed.militia || 0);
    return totalUnits <= availableMilitia;
}

function sendScoutingMission(
    state: HexMapState,
    target: Tile,
    originId: string,
    modal: ScoutingModal,
): boolean {
    const settlement = state.settlementsById[originId];
    if (!settlement) return false;
    const roster = buildUnitRoster(state.config);
    const deployed = getDeployedUnits(state, settlement.id);
    const selectedGroups = toGroups(modal.selectedUnits);
    const totalUnits = selectedGroups.reduce((sum, group) => sum + group.count, 0);
    if (totalUnits <= 0) return false;
    const weaponsRequired = selectedGroups.reduce((sum, group) => {
        const unit = roster.get(group.type);
        if (!unit) return sum;
        return sum + unit.weaponCost * group.count;
    }, 0);
    if (settlement.inventory.weapons < weaponsRequired) return false;
    if (settlement.inventory.food < modal.selectedFood) return false;
    const availableMilitia = getAvailableUnitCount(settlement, 'militia', deployed.militia || 0);
    if (totalUnits > availableMilitia) return false;

    const distance = hexDistance(settlement.tilePosition, target.position);
    const travelTicks = getScoutingTravelTicks(distance, state.config.scouting);
    state.scoutingIdCounter += 1;
    const missionId = `scout_${state.scoutingIdCounter}`;
    settlement.inventory.weapons -= weaponsRequired;
    settlement.inventory.food -= modal.selectedFood;

    state.scoutingMissions.push({
        id: missionId,
        fromSettlementId: originId,
        targetTileId: target.id,
        currentTileId: settlement.tileId,
        route: [],
        routeIndex: 0,
        units: selectedGroups.map((group) => ({ ...group })),
        initialUnits: selectedGroups.map((group) => ({ ...group })),
        plan: [],
        foodSupplies: modal.selectedFood,
        weaponsIssued: weaponsRequired,
        distance,
        travelTicks,
        startDelay: Math.max(0, Math.round(state.config.scouting.baseTicks)),
        stepTicks: Math.max(1, Math.round(state.config.scouting.ticksPerHex)),
        progress: 0,
        status: 'traveling',
        createdAt: state.simulation.tick,
    });

    return true;
}
